import { groupBy, findLastIndex, omit } from 'lodash';
import type { Document, TopLevelBlock, TopLevelBlockEnum } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';

const isSectionHeading = (nodeType: TopLevelBlockEnum) => nodeType === BLOCKS.HEADING_3;

/**
 * Group nodes by the index of the closest H3.
 * @note orphan nodes will be under key '-1'.
 */
const groupByClosestH3 = (blocks: TopLevelBlock[]): Record<string, TopLevelBlock[]> => {
  let i = -1;
  return groupBy(blocks, ({ nodeType }) => {
    i += 1;
    if (isSectionHeading(nodeType)) return i;
    const closestHeadingIndex = findLastIndex(blocks.slice(0, i), (item) => isSectionHeading(item.nodeType));
    return closestHeadingIndex;
  });
};

/**
 * Chunk RichText blocks by H3 into sections as a `Document` list that can be used with `renderRichTextContent()`.
 * @note blocks that can't be grouped into sections will be orphans.
 */
export const chunkRichTextBlocksInSections = (
  blocks: TopLevelBlock[]
): [h3Sections: Document[], orphanBlocks?: TopLevelBlock[]] => {
  const indexedSections = groupByClosestH3(blocks);
  const validSections = omit(indexedSections, '-1');
  const orphanBlocks = indexedSections['-1'];

  const h3Sections = Object.values(validSections).map(
    (content) =>
      ({
        nodeType: BLOCKS.DOCUMENT,
        content,
        data: {},
      }) as const
  );

  return [h3Sections, orphanBlocks];
};
